<template>
    <div>
        <div class="d-flex">
            <v-alert type="info" text dense>Changes will take effect after the application is restarted.</v-alert>
        </div>
        <v-skeleton-loader class="mx-auto" type="table" v-if="fetchingMountedStorageList"></v-skeleton-loader>
        <template v-else>
            <div class="d-flex align-center">
                <space-attach-storage-dialog
                    v-if="canMountStorage"
                    :space-id="resolvedSpaceId"
                    :dialog-max-width="dialogMaxWidth"
                    @attached="fetchMountedStorageList">
                    <template v-slot:activator="{ on, attrs, open }">
                        <v-btn color="primary" class="mr-2" v-bind="attrs" v-on="on" @click="open">
                            <v-icon left>mdi-connection</v-icon>
                            <span>Storage</span>
                        </v-btn>
                    </template>
                </space-attach-storage-dialog>
                <v-spacer></v-spacer>
                <v-text-field
                    placeholder="Search for an attached storage..."
                    autofocus
                    v-model="search"
                    clearable
                    hide-details
                    prepend-inner-icon="mdi-magnify "
                    solo
                    flat
                    dense
                    class="mr-2" />
                <v-btn :loading="fetchingMountedStorageList" @click="fetchMountedStorageList" icon><v-icon>refresh</v-icon></v-btn>
            </div>
            <v-data-table
                :items="mountedStorageList"
                item-key="amid"
                :headers="headers"
                :search="search"
                :options="tableOptions"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ pagination, options, updateOptions }">
                    <v-data-footer
                        :pagination="pagination"
                        :options="options"
                        @update:options="updateOptions"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                        class="no-border" />
                </template>
                <template v-slot:no-data>
                    <span>No additional storage has been attached yet. Click attach to attach a new storage.</span>
                </template>
                <template v-slot:[`item.long_id`]="{ item }">
                    <span class="text-body-1">{{ item.long_id }}</span>
                </template>
                <template v-slot:[`item.quota_gib`]="{ item }">{{ item.quota_gib }} GiB</template>
                <template v-slot:[`item.read_only`]="{ item }">
                    {{ item.read_only ? 'Read only' : 'Read/Write' }}
                </template>
                <template v-slot:[`item.mount_path`]="{ item }">
                    <code class="text-body-2">{{ item.mount_path }}</code>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <copy-to-clipboard :textToCopy="item.mount_path" buttonName="Copy path to clipboard" type="listItem" />
                            <v-list-item v-if="canMountStorage" @click="openEditMountDialog(item)">
                                <v-list-item-icon>
                                    <v-progress-circular v-if="editingMount === item.amid" size="18" width="2" indeterminate color="secondary" />
                                    <v-icon v-else color="secondary">mdi-pencil-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="secondary--text mr-5">Edit</v-list-item-title>
                            </v-list-item>
                            <v-divider v-if="canMountStorage" class="my-2"></v-divider>
                            <v-list-item v-if="canMountStorage" @click="openDeleteDialog(item)">
                                <v-list-item-icon>
                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title class="error--text mr-5">Remove</v-list-item-title>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
            </v-data-table>
        </template>
        <v-dialog v-model="editMountDialog" :max-width="dialogMaxWidth" lazy-validation persistent>
            <v-card>
                <v-card-title class="flex-nowrap align-start">
                    <span>Edit attached storage</span>

                    <v-spacer></v-spacer>
                    <v-btn @click="editMountDialog = false" icon><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="mb-0">
                    <v-form v-model="mountEditFormIsValid" ref="mountEditForm">
                        <v-text-field v-model="mountPoint" outlined dense :rules="[rules.required, rules.validChars, rules.unique]">
                            <template v-slot:label>Path</template>
                        </v-text-field>
                        <div class="mb-1">Storage will be accessible under</div>
                        <code class="d-flex">
                            {{ mountBasePath }}/
                            <b>{{ mountPoint }}</b>
                        </code>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-checkbox v-model="mountReadOnly" :disabled="selectedStorageOptionObject?.force_read_only">
                        <template v-slot:label>Read only mode</template>
                    </v-checkbox>
                    <v-spacer></v-spacer>

                    <v-btn text color="primary" @click="sendPatchRequest()" :disabled="!mountEditFormIsValid" :loading="editingMount === selectedMount?.amid">
                        Edit
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import eventBus from '@/components/EventBus'
import { sortBy } from 'lodash'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as filesStorageErrors from '@/modules/files/errors'
import * as integrationsErrors from '@/modules/user/errors/integrations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(filesStorageErrors, integrationsErrors, lifecycleErrors, permissionsErrors, spaceErrors)

const CopyToClipboard = () => import('@/components/CopyToClipboard.vue')
const SpaceAttachStorageDialog = () => import('@/modules/space/components/SpaceAttachStorageDialog.vue')

export default {
    props: {
        spaceId: {
            type: [String, Number],
            default: null
        },
        isSpaceAdminOverride: {
            type: Boolean,
            default: null
        }
    },
    components: { CopyToClipboard, SpaceAttachStorageDialog },
    data() {
        return {
            fetchingMountedStorageList: false,
            storageOptions: [],
            headers: [
                { text: 'Name', value: 'long_id', align: 'left' },
                { text: 'Access', value: 'read_only', align: 'left' },
                { text: 'Quota', value: 'quota_gib', align: 'left' },
                { text: 'Path', value: 'mount_path', align: 'right' },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ],
            itemsPerPageOptions: [25, 50, 100, -1],
            tableOptions: {
                page: 0,
                itemsPerPage: 25,
                pageStart: 1,
                pageStop: 25,
                itemsLength: 25
            },
            mountedStorageList: [],
            rules: {
                required: value => !!value || 'This field is required',
                validChars: p => /^[a-zA-Z0-9-_]*$/g.test(p) || 'Please use only letters, numbers, underscores and dashes',
                unique: value => !this.mountPoints.includes(value) || 'A storage is already mounted on that path'
            },
            search: '',
            selectedMount: null,
            editMountDialog: false,
            mountEditFormIsValid: true,
            selectedStorageOption: null,
            mountPoint: '',
            mountReadOnly: false,
            deletingMount: false,
            editingMount: null,
            dialogMaxWidth: '640px'
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isSpaceAdmin', 'isSpaceArchived']),
        resolvedSpaceId() {
            return this.spaceId || this.$route.params.sid
        },
        canMountStorage() {
            if (this.isSpaceAdminOverride !== null) {
                return this.isSpaceAdminOverride
            }
            return this.isSpaceAdmin
        },
        mountPoints() {
            const points = this.mountedStorageList.map(mount => this.trimMountPath(mount.mount_path))
            if (this.selectedMount) return points.filter(point => point !== this.trimMountPath(this.selectedMount.mount_path))
            return points
        },
        selectedStorageOptionObject() {
            return this.storageOptions.find(option => option.afsid === this.selectedStorageOption)
        },
        mountBasePath() {
            return this.storageOptions[0]?.base_path
        }
    },
    methods: {
        async fetchStorageOptions() {
            if (!this.resolvedSpaceId) {
                return
            }
            const endpoint = `/spaces/${this.resolvedSpaceId}/afs`
            try {
                const { data } = await this.$axios.get(endpoint)
                this.storageOptions = sortBy(data, 'long_id')
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'There was an error fetching available storage infromation')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.fetchingMountedStorageList = false
        },
        async fetchMountedStorageList() {
            if (!this.resolvedSpaceId) {
                this.mountedStorageList = []
                return
            }
            this.fetchingMountedStorageList = true
            const endpoint = `/spaces/${this.resolvedSpaceId}/mounts`
            try {
                const { data } = await this.$axios.get(endpoint)
                this.mountedStorageList = data
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, error.response?.data?.debug || 'There was an error fetching mounted storage infromation')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.fetchingMountedStorageList = false
        },
        async sendDeleteRequest() {
            this.deletingMount = this.selectedMount.amid
            const endpoint = `/lfs/afs/mounts/${this.deletingMount}`
            try {
                await this.$axios.delete(endpoint)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'There was an error deleting mounted storage infromation')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.selectedMount = null
            this.deletingMount = false
            this.fetchMountedStorageList()
        },
        async sendPatchRequest() {
            this.editingMount = this.selectedMount.amid
            const endpoint = `/lfs/afs/mounts/${this.editingMount}`
            const body = {}
            if (this.trimMountPath(this.selectedMount.mount_path) !== this.mountPoint) body.mount_path = this.mountPoint
            if (this.selectedMount.read_only !== this.mountReadOnly) body.read_only = this.mountReadOnly
            try {
                await this.$axios.patch(endpoint, body)
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'There was an error editing mount settings')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarTimeout: 5000,
                        snackBarIcon: 'error'
                    })
                }
            }
            this.editingMount = null
            this.editMountDialog = false
            this.$refs.mountEditForm.reset()
            this.mountReadOnly = false
            this.fetchMountedStorageList()
        },
        async openEditMountDialog(mount) {
            if (this.storageOptions.length === 0) await this.fetchStorageOptions()
            this.selectedMount = mount
            this.selectedStorageOption = this.storageOptions.find(option => option.afsid === mount.afsid).afsid
            this.editMountDialog = true
        },
        closeEditMountDialog() {
            this.editMountDialog = false
            this.selectedMount = null
            this.selectedStorageOption = null
            this.mountPoint = ''
        },
        openDeleteDialog(mount) {
            this.selectedMount = mount
            this.$store.dispatch('showGlobalDialog', {
                dialogText: `Are you sure you would like to detach this storage?`,
                dialogAction: ['delete', 'cancel'],
                dialogTitle: 'Confirm detach'
            })
        },
        selectStorageOption(option) {
            this.selectedStorageOption = option
        },
        trimMountPath(path) {
            return path.replace(`${this.mountBasePath}/`, '')
        }
    },
    watch: {
        spaceId() {
            this.fetchMountedStorageList()
        },
        selectedStorageOptionObject(to) {
            if (to.force_read_only) this.mountReadOnly = true
        },
        selectedMount(to) {
            this.mountReadOnly = to?.read_only
            this.mountPoint = this.trimMountPath(to?.mount_path)
        }
    },
    created() {
        this.fetchMountedStorageList()
        eventBus.$on('global-dialog-accept', async () => {
            this.sendDeleteRequest()
        })
        eventBus.$on('global-dialog-decline', async () => {
            this.selectedMount = null
        })
    }
}
</script>
